'use client';

import { useEffect } from 'react';

/**
 * Bay Reveal scrub fallback for browsers WITHOUT CSS scroll-driven
 * animations (iOS/iPadOS Safari before 26 — the founder's iPhone 11 report,
 * 2026-07-21). Verified in WebKit 26.5 that supporting browsers animate
 * natively and never activate this island.
 *
 * Mechanism: adds `.scrub` to `.landing` and mirrors the scroll position
 * into one custom property (--syvh, scroll offset in vh as a number).
 * marketing.css drives the SAME reveal keyframes from it via the
 * paused-animation + negative-delay technique — all motion stays in CSS;
 * this island never touches styles per element.
 *
 * No-ops entirely (no class, no listeners) when animation-timeline is
 * supported or prefers-reduced-motion is set (reduced motion keeps the
 * static revealed composition).
 */
export default function ScrollScrub() {
  useEffect(() => {
    // No CSS.supports (test DOMs, museum browsers) → can't verify, stay on
    // the static fallback; every real SDA-less browser has CSS.supports.
    if (
      typeof CSS === 'undefined' ||
      typeof CSS.supports !== 'function' ||
      CSS.supports('animation-timeline: scroll()') ||
      matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }
    const landing = document.querySelector<HTMLElement>('.landing');
    if (!landing) return undefined;

    landing.classList.add('scrub');
    let rafId = 0;
    const write = () => {
      rafId = 0;
      landing.style.setProperty(
        '--syvh',
        ((window.scrollY / window.innerHeight) * 100).toFixed(2),
      );
    };
    const schedule = () => {
      if (!rafId) rafId = requestAnimationFrame(write);
    };
    write();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      landing.classList.remove('scrub');
      landing.style.removeProperty('--syvh');
    };
  }, []);

  return null;
}
